/**
 * useEditorUi
 *
 * Owns the cross-cutting UI shell state for the editor:
 * - sectionsExpanded : accordion state of the 15 collapsible editor sections
 * - isSidebarOpen / isAtsSidebarOpen / isVisuelSidebarOpen : desktop panel toggles
 * - mobileTab / mobilePreviewSubTab : mobile bottom navigation
 * - layoutTemplate : which layout (ats/moderne/classique/pro) is rendered
 * - previewEngine  : 'react' or 'typst' preview renderer
 * - theme  : 'light' | 'dark' (applied to <html data-theme>)
 * - locale : 'fr' | 'en' (applied to <html lang>)
 *
 * All persistence to localStorage is owned here. layoutTemplate and
 * previewEngine are persisted under BOTH a global key and a per-template key
 * to stay behavior-identical with the previous inline App.tsx code.
 */

import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { safeLocalStorageGet, safeLocalStorageSet, safeLocalStorageSetJson, warnOnQuotaOnce } from '../lib/safe-storage';

export type SectionKey =
  | 'profilCommun'
  | 'optimisationAts'
  | 'options'
  | 'profil'
  | 'experiences'
  | 'formation'
  | 'plus'
  | 'modelesCv'
  | 'template'
  | 'offreEmploiCible'
  | 'scorePreview'
  | 'analyseCompleteAts'
  | 'aiAnalyze'
  | 'atsGenerate'
  | 'coverLetter';

export type MobileTab = 'edition' | 'ats' | 'preview';
export type MobilePreviewSubTab = 'apercu' | 'visuel';
export type LayoutTemplate = 'ats' | 'moderne' | 'classique' | 'pro';
export type PreviewEngine = 'react' | 'typst';
export type Theme = 'light' | 'dark';
export type Locale = 'fr' | 'en';

const DEFAULT_SECTIONS: Record<SectionKey, boolean> = {
  profilCommun: false,
  optimisationAts: false,
  options: false,
  profil: false,
  experiences: false,
  formation: false,
  plus: false,
  modelesCv: true, // only Versions is open by default
  template: false,
  offreEmploiCible: false,
  scorePreview: false,
  analyseCompleteAts: false,
  aiAnalyze: false,
  atsGenerate: false,
  coverLetter: false,
};

function initSectionsExpanded(): Record<SectionKey, boolean> {
  const saved = safeLocalStorageGet('cv_sections_expanded');
  if (saved) {
    try {
      return { ...DEFAULT_SECTIONS, ...(JSON.parse(saved) as Partial<Record<SectionKey, boolean>>) };
    } catch (e) {
      console.error('Failed to parse sections expanded', e);
    }
  }
  return { ...DEFAULT_SECTIONS };
}

function initLayoutTemplate(): LayoutTemplate {
  const active = safeLocalStorageGet('cv_active_template', 'classique') || 'classique';
  const saved = safeLocalStorageGet(`cv_layout_template_${active}`);
  if (saved === 'ats' || saved === 'moderne' || saved === 'classique' || saved === 'pro') {
    return saved;
  }
  return 'classique';
}

function initPreviewEngine(): PreviewEngine {
  const active = safeLocalStorageGet('cv_active_template', 'classique') || 'classique';
  const saved = safeLocalStorageGet(`cv_preview_engine_${active}`);
  return saved === 'typst' ? 'typst' : 'react';
}

function initTheme(): Theme {
  return (safeLocalStorageGet('cv_theme') as Theme) || 'dark';
}

function initLocale(): Locale {
  const saved = safeLocalStorageGet('cv_locale');
  return saved === 'en' ? 'en' : 'fr';
}

export interface UseEditorUiParams {
  /** Current template id — required to persist layoutTemplate and previewEngine
   *  under their per-template keys. */
  activeTemplate: string;
}

export interface UseEditorUiReturn {
  // sections
  sectionsExpanded: Record<SectionKey, boolean>;
  setSectionsExpanded: Dispatch<SetStateAction<Record<SectionKey, boolean>>>;
  toggleSection: (key: SectionKey) => void;

  // sidebars (desktop)
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
  isAtsSidebarOpen: boolean;
  setIsAtsSidebarOpen: Dispatch<SetStateAction<boolean>>;
  isVisuelSidebarOpen: boolean;
  setIsVisuelSidebarOpen: Dispatch<SetStateAction<boolean>>;
  isMiseEnPageOpen: boolean;
  setIsMiseEnPageOpen: Dispatch<SetStateAction<boolean>>;

  // mobile tabs
  mobileTab: MobileTab;
  setMobileTab: Dispatch<SetStateAction<MobileTab>>;
  mobilePreviewSubTab: MobilePreviewSubTab;
  setMobilePreviewSubTab: Dispatch<SetStateAction<MobilePreviewSubTab>>;

  // preview config
  layoutTemplate: LayoutTemplate;
  setLayoutTemplate: Dispatch<SetStateAction<LayoutTemplate>>;
  previewEngine: PreviewEngine;
  setPreviewEngine: Dispatch<SetStateAction<PreviewEngine>>;

  // app chrome
  theme: Theme;
  setTheme: Dispatch<SetStateAction<Theme>>;
  locale: Locale;
  setLocale: Dispatch<SetStateAction<Locale>>;
}

export function useEditorUi({ activeTemplate }: UseEditorUiParams): UseEditorUiReturn {
  const [sectionsExpanded, setSectionsExpanded] =
    useState<Record<SectionKey, boolean>>(initSectionsExpanded);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const v = safeLocalStorageGet('panel_edition'); return v !== null ? v === '1' : true;
  });
  const [isAtsSidebarOpen, setIsAtsSidebarOpen] = useState(() => {
    return safeLocalStorageGet('panel_ats') === '1';
  });
  const [isVisuelSidebarOpen, setIsVisuelSidebarOpen] = useState(() => {
    return safeLocalStorageGet('panel_visuel') === '1';
  });
  const [isMiseEnPageOpen, setIsMiseEnPageOpen] = useState(false);

  const [mobileTab, setMobileTab] = useState<MobileTab>('edition');
  const [mobilePreviewSubTab, setMobilePreviewSubTab] = useState<MobilePreviewSubTab>('apercu');

  const [layoutTemplate, setLayoutTemplate] = useState<LayoutTemplate>(initLayoutTemplate);
  const [previewEngine, setPreviewEngine] = useState<PreviewEngine>(initPreviewEngine);

  const [theme, setTheme] = useState<Theme>(initTheme);
  const [locale, setLocale] = useState<Locale>(initLocale);

  // ---- theme / locale side effects ----
  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSet('cv_theme', theme));
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSet('cv_locale', locale));
    document.documentElement.lang = locale === 'en' ? 'en' : 'fr';
  }, [locale]);

  // ---- layoutTemplate : global + per-template persistence ----
  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSet('cv_layout_template', layoutTemplate));
  }, [layoutTemplate]);

  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSet(`cv_layout_template_${activeTemplate}`, layoutTemplate));
  }, [layoutTemplate, activeTemplate]);

  // ---- previewEngine : global + per-template persistence ----
  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSet('cv_preview_engine', previewEngine));
  }, [previewEngine]);

  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSet(`cv_preview_engine_${activeTemplate}`, previewEngine));
  }, [previewEngine, activeTemplate]);

  // ---- panel open/close persistence ----
  useEffect(() => { void warnOnQuotaOnce(safeLocalStorageSet('panel_edition', isSidebarOpen ? '1' : '0')); }, [isSidebarOpen]);
  useEffect(() => { void warnOnQuotaOnce(safeLocalStorageSet('panel_ats', isAtsSidebarOpen ? '1' : '0')); }, [isAtsSidebarOpen]);
  useEffect(() => { void warnOnQuotaOnce(safeLocalStorageSet('panel_visuel', isVisuelSidebarOpen ? '1' : '0')); }, [isVisuelSidebarOpen]);

  // ---- sections toggle (writes localStorage synchronously) ----
  const toggleSection = (key: SectionKey) => {
    setSectionsExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      void warnOnQuotaOnce(safeLocalStorageSetJson('cv_sections_expanded', next));
      return next;
    });
  };

  return {
    sectionsExpanded,
    setSectionsExpanded,
    toggleSection,
    isSidebarOpen,
    setIsSidebarOpen,
    isAtsSidebarOpen,
    setIsAtsSidebarOpen,
    isVisuelSidebarOpen,
    setIsVisuelSidebarOpen,
    isMiseEnPageOpen,
    setIsMiseEnPageOpen,
    mobileTab,
    setMobileTab,
    mobilePreviewSubTab,
    setMobilePreviewSubTab,
    layoutTemplate,
    setLayoutTemplate,
    previewEngine,
    setPreviewEngine,
    theme,
    setTheme,
    locale,
    setLocale,
  };
}
